import {
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_TRANSMIT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_DRAFT_CANCELLED,
    DOSSIER_STATUS_TRANSMITTED_CANCELLED,
    DOSSIER_STATUS_SUSPENDED,
    DOSSIER_STATUS_ARCHIVED,
    DOSSIER_STATUS_REGULS,
    DOSSIER_STATUS_REJECTED_S2,
    DOSSIER_STATUS_REJECTED_S4,
    DOSSIER_STATUS_DONE,
    DOSSIER_STATUS_VALIDATED,
    DOSSIER_STATUS_TO_CHECK,
    DOSSIER_STATUS_CANCELLED,
    DOSSIER_STATUS_WAIT_FOR_PROVISION,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/status';
import {
    PROCESS_STATUS_STYLE_FAILED,
    PROCESS_STATUS_STYLE_DONE,
    PROCESS_STATUS_STYLE_ERROR,
    PROCESS_STATUS_STYLE_ONGOING,
    PROCESS_STATUS_STYLE_PENDING,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/validators';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faCircleCheck,
    faCircleExclamation,
    faCirclePause,
    faCircleX,
    faClock,
    faMagnifyingGlass,
    faPen,
    faSend,
} from '@fortawesome/sharp-regular-svg-icons';
import { NamedIconsType } from '@europrocurement/l2d-icons';
import { type Palette, type PaletteColor, type SxProps } from '@mui/material';
import { type ComponentColorResult } from '@europrocurement/flexy-components';
import L2DTheme from '../Theme';

export type StatusStyleType = {
    color: string;
    colorPalette: PaletteColor;
    paletteString: keyof Palette;
    icon: string;
    iconProp: IconProp;
    namedIcon: NamedIconsType;
    sxStatus: SxProps;
};

const useStatusStyle = () => {
    const { palette } = L2DTheme();

    const palettePrimary = palette.primary as PaletteColor;
    const palettePurple = palette.purple as PaletteColor;
    const paletteAmber = palette.amber as PaletteColor;
    const paletteSuccess = palette.success as PaletteColor;
    const paletteGrey = palette.blueGrey as PaletteColor;
    const paletteDeepOrange = palette.deepOrange as PaletteColor;
    const paletteError = palette.error as PaletteColor;

    const primaryMain = palettePrimary.main;
    const purple = palettePurple?.main || '#7A45E2';
    const amber = paletteAmber?.main || '#FF8229';
    const success = paletteSuccess.main;
    const deepOrange = paletteDeepOrange.main;
    const error = paletteError.main;
    const blueGreyMain = paletteGrey?.main || palette.grey[700];

    const tokenSx = (token: ComponentColorResult): SxProps => ({
        ...token.glassyStyle,
        color: token.textColor,
    });

    const STATUS_STYLE_FORMALIST = {
        color: primaryMain,
        colorPalette: palettePrimary,
        paletteString: 'primary' as keyof Palette,
        icon: 'fa-send',
        iconProp: faSend,
        namedIcon: {
            name: 'status_todo',
            displayName: 'A faire',
            props: {
                icon: faSend,
            },
        },
        sxStatus: tokenSx(palette.status.formalist),
    };

    const STATUS_STYLE_FORMALIST_ACTION = {
        color: deepOrange,
        colorPalette: paletteDeepOrange,
        paletteString: 'deepOrange' as keyof Palette,
        icon: 'fa-magnifying-glass',
        iconProp: faMagnifyingGlass,
        namedIcon: {
            name: 'status_formalist_action',
            displayName: 'Erreur',
            props: {
                icon: faMagnifyingGlass,
            },
        },
        sxStatus: tokenSx(palette.status.formalistAction),
    };

    const STATUS_STYLE_VALIDATOR = {
        color: purple,
        colorPalette: palettePurple,
        paletteString: 'purple' as keyof Palette,
        icon: 'fa-clock',
        iconProp: faClock,
        namedIcon: {
            name: 'status_validator',
            displayName: 'En attente',
            props: {
                icon: faClock,
            },
        },
        sxStatus: tokenSx(palette.status.validator),
    };

    const STATUS_STYLE_CLIENT = {
        color: primaryMain,
        colorPalette: palettePrimary,
        paletteString: 'primary' as keyof Palette,
        icon: 'fa-pen',
        iconProp: faPen,
        namedIcon: {
            name: 'status_client',
            displayName: 'Status client',
            props: {
                icon: faPen,
            },
        },
        sxStatus: tokenSx(palette.status.client),
    };

    const STATUS_STYLE_CLIENT_ACTION = {
        color: amber,
        colorPalette: paletteAmber,
        paletteString: 'amber' as keyof Palette,
        icon: 'fa-circle-exclamation',
        iconProp: faCircleExclamation,
        namedIcon: {
            name: 'status_client_action',
            displayName: 'Action',
            props: {
                icon: faCircleExclamation,
            },
        },
        sxStatus: tokenSx(palette.status.clientAction),
    };

    const STATUS_STYLE_SUCCESS = {
        color: success,
        colorPalette: paletteSuccess,
        paletteString: 'success' as keyof Palette,
        icon: 'fa-circle-check',
        iconProp: faCircleCheck,
        namedIcon: {
            name: 'status_success',
            displayName: 'Succès',
            props: {
                icon: faCircleCheck,
            },
        },
        sxStatus: tokenSx(palette.status.success),
    };

    const STATUS_STYLE_DANGER = {
        color: error,
        colorPalette: paletteError,
        paletteString: 'error' as keyof Palette,
        icon: 'fa-circle-x',
        iconProp: faCircleX,
        namedIcon: {
            name: 'status_danger',
            displayName: 'Erreur',
            props: {
                icon: faCircleX,
            },
        },
        sxStatus: tokenSx(palette.status.danger),
    };

    const STATUS_STYLE_INACTIV = {
        color: blueGreyMain,
        colorPalette: paletteGrey,
        paletteString: 'blueGrey' as keyof Palette,
        icon: 'fa-circle-pause',
        iconProp: faCirclePause,
        namedIcon: {
            name: 'status_inactiv',
            displayName: 'Inactif',
            props: {
                icon: faCirclePause,
            },
        },
        sxStatus: tokenSx(palette.status.inactive),
    };

    // Mapping between status id and status style
    const dossierStatusStyles: Record<number, StatusStyleType> = {
        // Formaliste
        [DOSSIER_STATUS_IN_PROGRESS]: STATUS_STYLE_FORMALIST,
        [DOSSIER_STATUS_AWAITING]: {
            ...STATUS_STYLE_FORMALIST,
            color: blueGreyMain,
            colorPalette: paletteGrey,
            paletteString: 'blueGrey' as keyof Palette,
            sxStatus: tokenSx(palette.status.inactive),
            namedIcon: {
                name: 'status_formaliste',
                displayName: 'En attente',
                props: {
                    icon: faClock,
                },
            },
        },

        // Formaliste action
        // TODO RENOMER VARIABLE -> DOSSIER_STATUS_VALIDATED
        [DOSSIER_STATUS_VALIDATED]: STATUS_STYLE_FORMALIST_ACTION,
        [DOSSIER_STATUS_TO_CHECK]: STATUS_STYLE_FORMALIST_ACTION,
        [DOSSIER_STATUS_REJECTED_S2]: STATUS_STYLE_FORMALIST_ACTION,

        // CLient
        [DOSSIER_STATUS_DRAFT]: STATUS_STYLE_CLIENT,

        // Client Action
        [DOSSIER_STATUS_INCOMPLETE]: STATUS_STYLE_CLIENT_ACTION,
        [DOSSIER_STATUS_WAIT_FOR_PROVISION]: STATUS_STYLE_CLIENT_ACTION,

        // Valideur
        [DOSSIER_STATUS_TRANSMIT]: STATUS_STYLE_VALIDATOR,
        [DOSSIER_STATUS_REGULS]: STATUS_STYLE_VALIDATOR,

        // Inactifs
        // 'Annulés'
        [DOSSIER_STATUS_SUSPENDED]: STATUS_STYLE_INACTIV,

        // Success / terminé
        [DOSSIER_STATUS_ARCHIVED]: STATUS_STYLE_SUCCESS,
        [DOSSIER_STATUS_DONE]: STATUS_STYLE_SUCCESS,

        // Erreur
        [DOSSIER_STATUS_DRAFT_CANCELLED]: STATUS_STYLE_DANGER,
        [DOSSIER_STATUS_REJECTED_S4]: STATUS_STYLE_DANGER,
        [DOSSIER_STATUS_TRANSMITTED_CANCELLED]: STATUS_STYLE_DANGER,
        [DOSSIER_STATUS_CANCELLED]: STATUS_STYLE_DANGER,
    };

    // Mapping between status id and status style
    const validatorStatusStyles: Record<string, StatusStyleType> = {
        // A faire
        [PROCESS_STATUS_STYLE_PENDING]: STATUS_STYLE_FORMALIST,
        // progression
        [PROCESS_STATUS_STYLE_ONGOING]: STATUS_STYLE_VALIDATOR,
        // Nécéssite action
        [PROCESS_STATUS_STYLE_ERROR]: STATUS_STYLE_FORMALIST_ACTION,
        // Terminé
        [PROCESS_STATUS_STYLE_DONE]: STATUS_STYLE_SUCCESS,
        // Erreur
        [PROCESS_STATUS_STYLE_FAILED]: STATUS_STYLE_DANGER,
    };

    const getDossierStatusStyle = (statusId: number): StatusStyleType => {
        if (dossierStatusStyles[statusId]) {
            return dossierStatusStyles[statusId];
        }

        // Default to STATUS_STYLE_INACTIV if statusId is not found
        console.warn(`Statut dossier manquant : ${statusId}`);
        return STATUS_STYLE_INACTIV;
    };

    const getValidatorStatusStyle = (statusStyle: string): StatusStyleType => {
        if (validatorStatusStyles[statusStyle]) {
            return validatorStatusStyles[statusStyle];
        }

        // Default to STATUS_STYLE_INACTIV if statusId is not found
        console.warn(`Statut valideur manquant : ${statusStyle}`);
        return STATUS_STYLE_INACTIV;
    };

    return {
        getDossierStatusStyle,
        getValidatorStatusStyle,
        STATUS_STYLE_FORMALIST,
    };
};
export default useStatusStyle;
